/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import {
	type CSSProperties,
	memo,
	type ReactNode,
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react';

import { cssMap, jsx } from '@compiled/react';

import useControlled from '@atlaskit/ds-lib/use-controlled';
import {
	type mainMinimumWidthVar,
	type UNSAFE_LayoutAreaState,
	UNSAFE_useLayoutAreaSizing,
} from '@atlaskit/navigation-system/layout/layout-sizing';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import { usePanelActionsInternal } from '../../panel-manager/panel-provider';
import { usePanelStateInternal } from '../../panel-manager/use-panel-state-internal';
import { PanelRouter } from '../../panels-renderer/panel-router';
import { PanelsRenderer } from '../../panels-renderer/panels-renderer';
import { PanelSlot } from './panel-slot';

// Live sizing belongs to the layout and slot, not the routed entry point. Panel
// context updates still reach the router when its content or params change.
const StablePanelRouter = memo(PanelRouter);

const localPanelTrackWidthVar = '--p_lclPnlTrackW';
const localPanelResizingVar = '--p_lclPnlRsz';
const localPanelMinWidthVar = '--p_lclPnlMinW';

const styles = cssMap({
	layout: {
		display: 'grid',
		containerType: 'inline-size',
		gridTemplateAreas: `'main'`,
		gridTemplateColumns: 'minmax(0, 1fr)',
		gridTemplateRows: '1fr',
		height: '100%',
		width: '100%',
		// While SideNav is collapsed, Main, Panel, and ChatPanel fit inline from 60rem.
		'@media (min-width: 60rem)': {
			gridTemplateAreas: `
                "main panel"
			`,
			gridTemplateRows: 'auto',
			// Share navigation-system's CSS variable name so both layouts honor the same minimum.
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
			gridTemplateColumns: `minmax(var(${'--n_mainMinW' satisfies typeof mainMinimumWidthVar}, 20rem), 1fr) clamp(var(${localPanelMinWidthVar}), var(${localPanelResizingVar}, var(${localPanelTrackWidthVar})), calc(100% - var(${'--n_mainMinW' satisfies typeof mainMinimumWidthVar}, 20rem)))`,
		},
		// Once SideNav becomes inline at 64rem, Panel overlays again until all four areas fit.
		'@media (min-width: 64rem)': {
			gridTemplateAreas: `'main'`,
			gridTemplateColumns: 'minmax(0, 1fr)',
			gridTemplateRows: '1fr',
		},
		'@media (min-width: 75rem)': {
			gridTemplateAreas: `
                "main panel"
			`,
			gridTemplateRows: 'auto',
			// Share navigation-system's CSS variable name so both layouts honor the same minimum.
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
			gridTemplateColumns: `minmax(var(${'--n_mainMinW' satisfies typeof mainMinimumWidthVar}, 20rem), 1fr) clamp(var(${localPanelMinWidthVar}), var(${localPanelResizingVar}, var(${localPanelTrackWidthVar})), calc(100% - var(${'--n_mainMinW' satisfies typeof mainMinimumWidthVar}, 20rem)))`,
		},
		overflow: 'auto',
	},
	mainContent: {
		gridArea: 'main',
		overflow: 'auto',
		display: 'flex',
		height: '100%',
		isolation: 'isolate',
	},
	panelUplift: {
		overflow: 'hidden',
	},
	managedInline: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors, @atlaskit/ui-styling-standard/no-nested-selectors
		'&&': {
			overflow: 'hidden',
			gridTemplateAreas: `'main panel'`,
			gridTemplateRows: 'auto',
			// Share navigation-system's CSS variable name so both layouts honor the same minimum.
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
			gridTemplateColumns: `minmax(var(${'--n_mainMinW' satisfies typeof mainMinimumWidthVar}, 20rem), 1fr) clamp(var(${localPanelMinWidthVar}), var(${localPanelResizingVar}, var(${localPanelTrackWidthVar})), calc(100% - var(${'--n_mainMinW' satisfies typeof mainMinimumWidthVar}, 20rem)))`,
		},
	},
	managedOverlay: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors, @atlaskit/ui-styling-standard/no-nested-selectors
		'&&': {
			overflow: 'hidden',
			gridTemplateAreas: `'main'`,
			gridTemplateColumns: 'minmax(0, 1fr)',
			gridTemplateRows: '1fr',
		},
	},
	panelClosed: {
		// LayoutWithPanel can remain mounted without an active panel. Do not reserve its
		// panel track until PanelRouter has content to render into it.
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors, @atlaskit/ui-styling-standard/no-nested-selectors
		'&&': {
			gridTemplateAreas: `'main'`,
			gridTemplateColumns: 'minmax(0, 1fr)',
			gridTemplateRows: '1fr',
		},
	},
});

export type LayoutWithPanelProps = {
	/**
	 * Panel width. Defaults to 365px, or 400px with `platform-dst-chat-panel-layout` enabled.
	 * Can be overridden via resizing.
	 */
	panelWidth?: number;
	onPanelWidthChange?: (panelWidth: number) => void;
	/**
	 * Minimum panel width. Defaults to 320px.
	 */
	panelMinWidth?: number;
	/**
	 * Optional application-defined maximum panel width.
	 */
	panelMaxWidth?: number;
	testId?: string;
	children: ReactNode;
	/**
	 * Called when resizing is complete.
	 * @param finalWidth number representing the new width of the panel in px
	 *
	 */
	onResizeEnd?: (finalWidth: number) => void;
};

export function LayoutWithPanel({
	children,
	panelWidth,
	onPanelWidthChange,
	panelMinWidth = 320,
	panelMaxWidth,
	testId,
	onResizeEnd,
}: LayoutWithPanelProps): JSX.Element {
	const manager = usePanelActionsInternal();
	const state = usePanelStateInternal();

	const [defaultPanelWidth] = useState(panelWidth ?? state.panelWidth);
	const [compactDefaultPanelWidth] = useState(panelWidth ?? 320);
	const isWidthPersistenceEnabled = fg('platform-dst-panel-system-width-persistence');
	const isControlled =
		isWidthPersistenceEnabled && panelWidth !== undefined && onPanelWidthChange !== undefined;
	const [persistedPanelWidth, setPersistedPanelWidth] = useControlled(
		isControlled ? panelWidth : undefined,
		() => defaultPanelWidth,
	);
	const panelSlotTestId = testId && `${testId}--panel-slot`;
	const layoutRef = useRef<HTMLDivElement | null>(null);
	const {
		state: managedSizing,
		getResizeBounds,
		startResize,
		resize,
		completeResize,
	} = UNSAFE_useLayoutAreaSizing({
		area: 'panel',
		config: {
			requestedWidth: isControlled ? panelWidth : undefined,
			defaultWidth: defaultPanelWidth,
			compactDefaultWidth: compactDefaultPanelWidth,
			minWidth: panelMinWidth,
			maxWidth: panelMaxWidth,
			isOpen: Boolean(state.activePanel),
		},
	});
	const panelTrackWidth = managedSizing?.width ?? persistedPanelWidth;
	const onInlineResizeEnd = useCallback(
		(finalWidth: number) => {
			if (!isWidthPersistenceEnabled) {
				setPersistedPanelWidth(finalWidth);
			}
		},
		[isWidthPersistenceEnabled, setPersistedPanelWidth],
	);
	const handleWidthChange = useCallback(
		(width: number) => {
			if (isWidthPersistenceEnabled) {
				setPersistedPanelWidth(width);
				onPanelWidthChange?.(width);
			}
		},
		[isWidthPersistenceEnabled, onPanelWidthChange, setPersistedPanelWidth],
	);
	useEffect(() => {
		manager.setPanelWidth(persistedPanelWidth);
	}, [manager, persistedPanelWidth]);

	const activePanelRef = useRef(state.activePanel);
	const managerRef = useRef(manager);

	// Keep refs up to date on every render so the cleanup always sees the latest values
	activePanelRef.current = state.activePanel;
	managerRef.current = manager;

	useEffect(() => {
		/**
		 * Close the active panel when this component unmounts.
		 * Ensures panels do not persist when navigating to a new page.
		 */
		return () => {
			if (activePanelRef.current) {
				managerRef.current.closePanel(activePanelRef.current.instanceId);
			}
		};
	}, []);

	return (
		<div
			css={[
				styles.layout,
				fg('platform-dst-motion-uplift-panel') && styles.panelUplift,
				managedSizing?.mode === 'inline' && styles.managedInline,
				managedSizing?.mode === 'overlay' && styles.managedOverlay,
				!state.activePanel && styles.panelClosed,
			]}
			data-testid={testId}
			ref={layoutRef}
			style={
				{
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-unsafe-values
					[localPanelTrackWidthVar]: `${panelTrackWidth}px`,
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-unsafe-values
					[localPanelMinWidthVar]: `${panelMinWidth}px`,
				} as CSSProperties
			}
		>
			<div css={styles.mainContent}>{children}</div>
			<PanelsRenderer>
				<PanelSlot
					defaultWidth={defaultPanelWidth}
					compactDefaultWidth={compactDefaultPanelWidth}
					initialInlineWidth={persistedPanelWidth}
					width={isControlled ? panelWidth : undefined}
					onWidthChange={handleWidthChange}
					layoutRef={layoutRef}
					managedSizing={managedSizing as UNSAFE_LayoutAreaState | undefined}
					getManagedResizeBounds={getResizeBounds}
					minWidth={panelMinWidth}
					maxWidth={panelMaxWidth}
					onManagedResizeStart={startResize}
					onManagedResize={resize}
					onManagedResizeEnd={completeResize}
					onInlineResizeEnd={onInlineResizeEnd}
					onResizeEnd={onResizeEnd}
					testId={panelSlotTestId}
				>
					<StablePanelRouter />
				</PanelSlot>
			</PanelsRenderer>
		</div>
	);
}
